import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { adminService } from "./adminApi";
import { Search, Save, User, Route, Lock, Unlock, RefreshCw, BookOpen } from "lucide-react";

interface StageChoice {
	value: string;
	label: string;
}

interface JourneyPreset {
	id: number;
	label: string;
	bap: string;
	lender: string;
	journey_type: string;
	version: number;
	journey: any[][];
}

interface BapContext {
	bap_name: string | null;
	business_name: string | null;
	director_name: string | null;
}

interface JourneyStateData {
	id: number;
	user_id: number;
	username: string;
	current_stage: string;
	last_route: string;
	meta: Record<string, any>;
	checkpoint_template: any[][];
	current_checkpoint_index: number;
	is_locked: boolean;
	created_at: string | null;
	updated_at: string | null;
	stage_choices: StageChoice[];
	bap_context: BapContext[];
}

// Sentinel values for the preset Select
const MANUAL_PRESET_VALUE = "__manual__";
const MULTI_MATCH_VALUE = "__multi__";

// Mirrors backend primary_app/journey.py STAGE_ROUTE_MAP
const STAGE_ROUTE_MAP: Record<string, string> = {
	OTP_LOGIN: "/otp-login",
	WELCOME: "/welcome",
	REQUIREMENTS: "/requirements",
	PERSONAL_INFO: "/personal-info",
	BUSINESS_INFO: "/business-info",
	ADDITIONAL_INFO: "/additional-details",
	TRIGGER_VERIFICATION_SET_A: "/ruleset-a",
	LOAN_APPLICATION_UNABLE: "/loan-application-unable",
	TRIGGER_VERIFICATION_SET_B: "/ruleset-b",
	ALL_OFFERS: "/all-offers",
	LOAN_OFFER: "/loan-offer",
	FINAL_OFFER: "/final-offer",
	SELFIE_PHOTO_AND_LOCATION: "/selfie-photo-loc-verification",
	SHOP_ONLY_PHOTO_AND_LOCATION: "/shop-only-photo-loc-verification",
	SHOP_SELFIE_PHOTO_AND_LOCATION: "/shop-selfie-photo-loc-verification",
	SHOP_VIDEO_AND_LOCATION: "/shop-video-loc-verification",
	DIGI_CHECK: "/digi-check",
	MAS_DIGIO_JOURNEY: "/mas-digio-journey",
	DIGIO_DIGILOCKER_CALLBACK: "/kyc-digio-digilocker/callback",
	SELFIE_AADHAAR_MATCH: "/selfie-aadhaar-match",
	FINAL_KFS: "/final-kfs",
	BANK_ACCOUNT_AUTH: "/bank-account-authentication",
	ENACH_MANDATE: "/setup-mandates",
	UPI_MANDATE: "/setup-mandates",
	VERIFICATION_PENDING: "/waiting-for-updates",
	DIGIO_ESIGN: "/esign",
	DOC_PUSH: "/doc-push",
	JOURNEY_ERROR: "/journey-error",
	COMPLETED: "/loan-success",
	REJECTED: "/final-reject-state",
	LOAN_AMOUNT_CONFIRMATION: "/loan-amount-confirmation",
	EXPERIAN_CONSENT: "/experian-consent-journey",
	LOAN_AMOUNT_WHITESPACE: "/loan-amount",
	TRIGGER_VERIFICATION_SET_WHITESPACE: "/ruleset-whitespace",
	SUCCESS_WHITESPACE: "/success-whitespace",
	REJECTED_WHITESPACE: "/rejected-whitespace",
	BUSINESS_INFO_WHITESPACE: "/whitespace/business-info",
	PRE_APPROVED_AMOUNT_WHITESPACE: "/whitespace/pre-approved-amount",
	CURATED_OFFERS_WHITESPACE: "/whitespace/curating-offers",
	CHECK_PREAPPROVED: "/whitespace/check-preapproved",
};

// Sentinel for "user typed a custom route not in the map"
const CUSTOM_ROUTE_VALUE = "__custom__";

// Sorted dropdown options — deduplicated by route value, sorted alphabetically by route
const ROUTE_OPTIONS: { stage: string; route: string }[] = Object.entries(STAGE_ROUTE_MAP)
	.reduce<{ stage: string; route: string }[]>((acc, [stage, route]) => {
		if (!acc.some((o) => o.route === route)) acc.push({ stage, route });
		return acc;
	}, [])
	.sort((a, b) => a.route.localeCompare(b.route));

const SetJourney: React.FC = () => {
	const { toast } = useToast();

	// Search state
	const [userIdInput, setUserIdInput] = React.useState("");
	const [loading, setLoading] = React.useState(false);
	const [saving, setSaving] = React.useState(false);

	// Journey state data from API
	const [journeyData, setJourneyData] = React.useState<JourneyStateData | null>(null);

	// Preset list (loaded once on mount)
	const [presets, setPresets] = React.useState<JourneyPreset[]>([]);
	const [presetsLoading, setPresetsLoading] = React.useState(false);

	// Editable field states (local copies)
	const [currentStage, setCurrentStage] = React.useState("");
	const [lastRoute, setLastRoute] = React.useState("");
	// Dropdown selection for last_route
	// CUSTOM_ROUTE_VALUE → user typed a route not in STAGE_ROUTE_MAP
	// stage key string   → one of the known routes is selected
	const [selectedRouteStage, setSelectedRouteStage] = React.useState<string>(CUSTOM_ROUTE_VALUE);
	const [metaText, setMetaText] = React.useState("");
	const [checkpointTemplateText, setCheckpointTemplateText] = React.useState("");
	const [currentCheckpointIndex, setCurrentCheckpointIndex] = React.useState(0);
	const [isLocked, setIsLocked] = React.useState(true);

	// Preset dropdown selection
	// MANUAL_PRESET_VALUE  → user edited the textarea directly (no match)
	// MULTI_MATCH_VALUE    → textarea matches more than one preset
	// String(preset.id)    → exactly one preset matches
	const [selectedPresetId, setSelectedPresetId] = React.useState<string>(MANUAL_PRESET_VALUE);
	// All preset IDs whose journey matches the current textarea (may be >1)
	const [matchingPresetIds, setMatchingPresetIds] = React.useState<string[]>([]);

	// Validation errors
	const [metaError, setMetaError] = React.useState("");
	const [checkpointError, setCheckpointError] = React.useState("");

	// Load presets on mount
	React.useEffect(() => {
		const fetchPresets = async () => {
			setPresetsLoading(true);
			try {
				const res = await adminService.listJourneyPresets();
				setPresets(res.data.results || []);
			} catch {
				// Non-fatal — presets just won't be available
			} finally {
				setPresetsLoading(false);
			}
		};
		fetchPresets();
	}, []);

	// Whenever checkpointTemplateText or presets change, find ALL matching presets.
	// • 0 matches → MANUAL_PRESET_VALUE
	// • 1 match   → that preset's id (shown selected in the dropdown)
	// • 2+ matches → MULTI_MATCH_VALUE (shown as a special item; matchingPresetIds lists all)
	React.useEffect(() => {
		if (presets.length === 0) {
			setMatchingPresetIds([]);
			setSelectedPresetId(MANUAL_PRESET_VALUE);
			return;
		}
		try {
			const parsed = JSON.parse(checkpointTemplateText);
			if (!Array.isArray(parsed)) {
				setMatchingPresetIds([]);
				setSelectedPresetId(MANUAL_PRESET_VALUE);
				return;
			}
			const parsedStr = JSON.stringify(parsed);
			const matches = presets.filter((p) => JSON.stringify(p.journey) === parsedStr);
			const matchIds = matches.map((p) => String(p.id));
			setMatchingPresetIds(matchIds);
			if (matches.length === 0) {
				setSelectedPresetId(MANUAL_PRESET_VALUE);
			} else if (matches.length === 1) {
				setSelectedPresetId(matchIds[0]);
			} else {
				setSelectedPresetId(MULTI_MATCH_VALUE);
			}
		} catch {
			setMatchingPresetIds([]);
			setSelectedPresetId(MANUAL_PRESET_VALUE);
		}
	}, [checkpointTemplateText, presets]);

	// Sync lastRoute text → dropdown selection
	React.useEffect(() => {
		const match = Object.entries(STAGE_ROUTE_MAP).find(([, route]) => route === lastRoute);
		setSelectedRouteStage(match ? match[0] : CUSTOM_ROUTE_VALUE);
	}, [lastRoute]);

	// Auto-compute currentCheckpointIndex when currentStage or checkpointTemplateText changes
	React.useEffect(() => {
		if (!currentStage) return;
		try {
			const parsed = JSON.parse(checkpointTemplateText);
			if (!Array.isArray(parsed)) return;
			const idx = parsed.findIndex(
				(group: any) => Array.isArray(group) && group.includes(currentStage)
			);
			if (idx !== -1) {
				setCurrentCheckpointIndex(idx);
			}
		} catch {
			// Invalid JSON — leave index unchanged
		}
	}, [currentStage, checkpointTemplateText]);

	const handleCheck = async () => {
		const uid = parseInt(userIdInput.trim(), 10);
		if (!uid || isNaN(uid) || uid <= 0) {
			toast({ title: "Invalid User ID", description: "Please enter a valid numeric user ID.", variant: "destructive" });
			return;
		}
		setLoading(true);
		setJourneyData(null);
		setMetaError("");
		setCheckpointError("");
		try {
			const res = await adminService.getJourneyState(uid);
			const data: JourneyStateData = res.data;
			setJourneyData(data);
			setCurrentStage(data.current_stage);
			setLastRoute(data.last_route);
			setMetaText(JSON.stringify(data.meta, null, 2));
			const tplText = JSON.stringify(data.checkpoint_template, null, 2);
			setCheckpointTemplateText(tplText);
			setCurrentCheckpointIndex(data.current_checkpoint_index);
			setIsLocked(data.is_locked);
			// selectedPresetId will be synced automatically by the useEffect above
		} catch (error: any) {
			const msg = error.response?.data?.error || "Failed to fetch journey state";
			toast({ title: "Not Found", description: msg, variant: "destructive" });
		} finally {
			setLoading(false);
		}
	};

	// When a known route is chosen from the dropdown → set lastRoute text
	const handleRouteSelect = (stageKey: string) => {
		setSelectedRouteStage(stageKey);
		if (stageKey === CUSTOM_ROUTE_VALUE) return;
		const route = STAGE_ROUTE_MAP[stageKey];
		if (route) setLastRoute(route);
	};

	// When a preset is chosen from the dropdown → populate the textarea
	const handlePresetSelect = (presetIdStr: string) => {
		setSelectedPresetId(presetIdStr);
		if (presetIdStr === MANUAL_PRESET_VALUE) return;
		const preset = presets.find((p) => String(p.id) === presetIdStr);
		if (!preset) return;
		const tplText = JSON.stringify(preset.journey, null, 2);
		setCheckpointTemplateText(tplText);
		setCheckpointError("");
	};

	// When the textarea is edited → validate only; preset matching is handled by the useEffect
	const handleCheckpointChange = (val: string) => {
		setCheckpointTemplateText(val);
		try {
			const parsed = JSON.parse(val);
			if (!Array.isArray(parsed)) {
				setCheckpointError("Must be a JSON array");
				return;
			}
			setCheckpointError("");
		} catch {
			setCheckpointError("Invalid JSON");
		}
	};

	const validateJson = (text: string): { valid: boolean; parsed?: any } => {
		try {
			return { valid: true, parsed: JSON.parse(text) };
		} catch {
			return { valid: false };
		}
	};

	const handleMetaChange = (val: string) => {
		setMetaText(val);
		const result = validateJson(val);
		setMetaError(result.valid ? "" : "Invalid JSON");
	};

	const handleSave = async () => {
		if (!journeyData) return;

		const metaResult = validateJson(metaText);
		if (!metaResult.valid) {
			setMetaError("Invalid JSON — please fix before saving.");
			toast({ title: "Invalid JSON in Meta", variant: "destructive" });
			return;
		}
		setMetaError("");

		const checkpointResult = validateJson(checkpointTemplateText);
		if (!checkpointResult.valid) {
			setCheckpointError("Invalid JSON — please fix before saving.");
			toast({ title: "Invalid JSON in Checkpoint Template", variant: "destructive" });
			return;
		}
		if (!Array.isArray(checkpointResult.parsed)) {
			setCheckpointError("Must be a JSON array.");
			toast({ title: "Checkpoint Template must be an array", variant: "destructive" });
			return;
		}
		setCheckpointError("");

		setSaving(true);
		try {
			await adminService.updateJourneyState(journeyData.user_id, {
				current_stage: currentStage,
				last_route: lastRoute,
				meta: metaResult.parsed,
				checkpoint_template: checkpointResult.parsed,
				current_checkpoint_index: currentCheckpointIndex,
				is_locked: isLocked,
			});
			toast({
				title: "Journey State Saved",
				description: `Successfully updated journey state for user ID ${journeyData.user_id}.`,
			});
			// Refresh
			const res = await adminService.getJourneyState(journeyData.user_id);
			const updated: JourneyStateData = res.data;
			setJourneyData(updated);
			const tplText = JSON.stringify(updated.checkpoint_template, null, 2);
			setMetaText(JSON.stringify(updated.meta, null, 2));
			setCheckpointTemplateText(tplText);
			// selectedPresetId will be synced automatically by the useEffect above
		} catch (error: any) {
			const msg = error.response?.data?.error || "Failed to save journey state";
			toast({ title: "Save Failed", description: msg, variant: "destructive" });
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="max-w-3xl w-full space-y-6">
			{/* Search Section */}
			<div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
				<h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
					<User className="h-4 w-4 text-intellend-green" />
					Lookup User Journey
				</h3>
				<div className="flex gap-3">
					<Input
						type="number"
						placeholder="Enter User ID (e.g. 42)"
						value={userIdInput}
						onChange={(e) => setUserIdInput(e.target.value)}
						onKeyDown={(e) => e.key === "Enter" && handleCheck()}
						className="font-mono max-w-xs"
						min={1}
					/>
					<Button
						onClick={handleCheck}
						disabled={loading || !userIdInput.trim()}
						className="bg-intellend-green hover:bg-intellend-green/90 text-white gap-2"
					>
						{loading ? (
							<RefreshCw className="h-4 w-4 animate-spin" />
						) : (
							<Search className="h-4 w-4" />
						)}
						{loading ? "Checking…" : "Check"}
					</Button>
				</div>
			</div>

			{/* Journey State Editor */}
			{journeyData && (
				<div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
					{/* User Info Header */}
					<div className="px-6 py-4 border-b bg-intellend-green/5">
						<div className="flex items-center justify-between flex-wrap gap-3">
							<div className="flex items-center gap-3">
								<div className="flex h-9 w-9 items-center justify-center rounded-full bg-intellend-green/10 text-intellend-green font-bold text-sm select-none">
									{journeyData.username.charAt(0).toUpperCase()}
								</div>
								<div>
									<p className="font-semibold text-gray-800">{journeyData.username}</p>
									<p className="text-xs text-gray-400 font-mono">ID: {journeyData.user_id} · State ID: {journeyData.id}</p>
								</div>
							</div>
							<div className="flex items-center gap-2 flex-wrap">
								{journeyData.updated_at && (
									<span className="text-xs text-gray-400">
										Last updated: {new Date(journeyData.updated_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
									</span>
								)}
								<Badge variant={isLocked ? "secondary" : "outline"} className="gap-1">
									{isLocked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
									{isLocked ? "Locked" : "Unlocked"}
								</Badge>
							</div>
						</div>

						{/* BAP / Business context rows */}
						{journeyData.bap_context.length > 0 && (
							<div className="mt-3 space-y-1.5">
								{journeyData.bap_context.map((ctx, i) => (
									<div
										key={i}
										className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs bg-white/70 border border-gray-100 rounded-md px-3 py-2"
									>
										{ctx.bap_name && (
											<span className="flex items-center gap-1 text-gray-500">
												<span className="font-medium text-gray-700">BAP:</span>
												{ctx.bap_name}
											</span>
										)}
										{ctx.business_name && (
											<span className="flex items-center gap-1 text-gray-500">
												<span className="font-medium text-gray-700">Business:</span>
												{ctx.business_name}
											</span>
										)}
										{ctx.director_name && (
											<span className="flex items-center gap-1 text-gray-500">
												<span className="font-medium text-gray-700">Director:</span>
												{ctx.director_name}
											</span>
										)}
									</div>
								))}
							</div>
						)}
					</div>

					<div className="p-6 space-y-5">
						{/* Current Stage */}
						<div className="space-y-1.5">
							<Label className="text-sm font-medium text-gray-700">Current Stage</Label>
							<Select value={currentStage} onValueChange={setCurrentStage}>
								<SelectTrigger className="w-full font-mono text-sm">
									<SelectValue placeholder="Select a stage" />
								</SelectTrigger>
								<SelectContent className="max-h-72">
									{journeyData.stage_choices.map((choice) => (
										<SelectItem key={choice.value} value={choice.value} className="font-mono text-xs">
											<span className="font-semibold">{choice.value}</span>
											<span className="ml-2 text-gray-400 font-sans font-normal">{choice.label}</span>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{/* Last Route */}
						<div className="space-y-2">
							<Label className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
								<Route className="h-3.5 w-3.5 text-gray-400" />
								Last Route
							</Label>
							{/* Known-route dropdown */}
							<Select value={selectedRouteStage} onValueChange={handleRouteSelect}>
								<SelectTrigger className="w-full font-mono text-sm bg-white">
									<SelectValue placeholder="Select a known route…" />
								</SelectTrigger>
								<SelectContent className="max-h-72">
									<SelectItem value={CUSTOM_ROUTE_VALUE} className="text-xs text-gray-400 italic">
										— Custom route —
									</SelectItem>
									{ROUTE_OPTIONS.map(({ stage, route }) => (
										<SelectItem key={stage} value={stage} className="font-mono text-xs">
											<span className="font-semibold">{stage}</span>
											<span className="ml-2 text-gray-400 font-sans font-normal">{route}</span>
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							{/* Manual text input — always visible */}
							<Input
								value={lastRoute}
								onChange={(e) => setLastRoute(e.target.value)}
								placeholder="/otp-login"
								className="font-mono text-sm"
							/>
							<p className="text-xs text-gray-400">
								Select a known stage route above, or type a custom path directly in the field.
							</p>
						</div>

						{/* Current Checkpoint Index */}
						<div className="space-y-1.5">
							<Label className="text-sm font-medium text-gray-700">Current Checkpoint Index</Label>
							<Input
								type="number"
								value={currentCheckpointIndex}
								onChange={(e) => setCurrentCheckpointIndex(parseInt(e.target.value, 10) || 0)}
								min={0}
								className="font-mono text-sm max-w-[120px]"
							/>
						</div>

						{/* Is Locked */}
						<div className="flex items-center justify-between p-4 rounded-lg border border-gray-100 bg-slate-50">
							<div>
								<p className="font-medium text-gray-800 text-sm">Journey Locked</p>
								<p className="text-xs text-gray-500 mt-0.5">
									When locked, the journey template can be updated by system events. When unlocked, it is fixed.
								</p>
							</div>
							<Switch
								checked={isLocked}
								onCheckedChange={setIsLocked}
								className="data-[state=checked]:bg-intellend-green"
							/>
						</div>

						{/* ── Checkpoint Template section ── */}
						<div className="space-y-3 rounded-lg border border-gray-200 p-4 bg-gray-50/60">
							<div className="flex items-center gap-2">
								<BookOpen className="h-4 w-4 text-intellend-green shrink-0" />
								<span className="text-sm font-semibold text-gray-800">Checkpoint Template</span>
							</div>

							{/* Preset Dropdown */}
							<div className="space-y-1.5">
								<Label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
									Load from Preset
								</Label>
								<Select
									value={selectedPresetId}
									onValueChange={handlePresetSelect}
									disabled={presetsLoading}
								>
									<SelectTrigger className="w-full text-sm bg-white">
										<SelectValue placeholder={presetsLoading ? "Loading presets…" : "Select a journey preset…"} />
									</SelectTrigger>
									<SelectContent className="max-h-72">
										<SelectItem value={MANUAL_PRESET_VALUE} className="text-xs text-gray-400 italic">
											— Manually edited —
										</SelectItem>
										{/* Shown only when the current JSON matches 2+ presets */}
										{selectedPresetId === MULTI_MATCH_VALUE && (
											<SelectItem value={MULTI_MATCH_VALUE} className="text-xs text-amber-600 italic" disabled>
												— Matches {matchingPresetIds.length} presets —
											</SelectItem>
										)}
										{presets.map((preset) => (
											<SelectItem key={preset.id} value={String(preset.id)} className="text-xs">
												{preset.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								{/* Multi-match info line */}
								{selectedPresetId === MULTI_MATCH_VALUE && matchingPresetIds.length > 1 && (
									<p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 leading-relaxed">
										<span className="font-semibold">Multiple presets share this template:</span>{" "}
										{matchingPresetIds
											.map((id) => presets.find((p) => String(p.id) === id)?.label)
											.filter(Boolean)
											.join(" · ")}
									</p>
								)}
								<p className="text-xs text-gray-400">
									Selecting a preset fills the JSON below. Editing the JSON directly switches the dropdown to "Manually edited".
								</p>
							</div>

							{/* Raw JSON Textarea */}
							<div className="space-y-1.5">
								<Label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
									Raw JSON
									<span className="ml-2 normal-case font-normal text-gray-400">(array of arrays)</span>
								</Label>
								<Textarea
									value={checkpointTemplateText}
									onChange={(e) => handleCheckpointChange(e.target.value)}
									className={`font-mono text-xs min-h-[180px] resize-y bg-white ${checkpointError ? "border-red-400 focus-visible:ring-red-400" : ""}`}
									spellCheck={false}
								/>
								{checkpointError && (
									<p className="text-xs text-red-500">{checkpointError}</p>
								)}
								<p className="text-xs text-gray-400">
									Example: <code className="bg-gray-100 px-1 rounded">[["OTP_LOGIN"], ["PERSONAL_INFO"], ["SELFIE_PHOTO_AND_LOCATION", "SHOP_ONLY_PHOTO_AND_LOCATION"]]</code>
								</p>
							</div>
						</div>

						{/* Meta JSON */}
						<div className="space-y-1.5">
							<Label className="text-sm font-medium text-gray-700">
								Meta
								<span className="ml-2 text-xs font-normal text-gray-400">(JSON object)</span>
							</Label>
							<Textarea
								value={metaText}
								onChange={(e) => handleMetaChange(e.target.value)}
								className={`font-mono text-xs min-h-[120px] resize-y ${metaError ? "border-red-400 focus-visible:ring-red-400" : ""}`}
								spellCheck={false}
							/>
							{metaError && (
								<p className="text-xs text-red-500">{metaError}</p>
							)}
						</div>

						{/* Save Button */}
						<div className="flex justify-end pt-2 border-t border-gray-100">
							<Button
								onClick={handleSave}
								disabled={saving || !!metaError || !!checkpointError}
								className="bg-intellend-green hover:bg-intellend-green/90 text-white gap-2"
							>
								{saving ? (
									<RefreshCw className="h-4 w-4 animate-spin" />
								) : (
									<Save className="h-4 w-4" />
								)}
								{saving ? "Saving…" : "Save Changes"}
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default SetJourney;
